import { create } from "zustand";
import { supabase } from "@/integrations/supabase/client";
import { dashboardKPIsSchema, dashboardHeatmapSchema, dashboardGrowthSchema, dashboardMemberEvolutionSchema, dashboardInsightsSchema, dashboardCoachPerformanceSchema } from '@/services/dashboardResponseSchemas';
import { getDashboardGranularity, type DashboardPeriod, type DateRange } from "@/utils/dashboardDateUtils";
import {
  dashboardScopeKey,
  isLatestDashboardRequest,
  runDashboardRequest,
  type DashboardRequestScope,
} from "@/services/dashboardRequestCoordinator";

interface HeatmapCell { day: number; hour: number; bookings: number; avgCapacity: number; occupationRate: number }
interface GrowthDataPoint { month: string; members_count: number; revenue: number; previous_members_count: number; previous_revenue: number }
interface MemberEvolutionPoint { bucket: string; new_members: number; renewals: number }
interface InsightData { type: string; title: string; description: string; data: unknown }
interface CoachPerformance { coach_id: string; coach_name: string; classes_count: number; total_bookings: number; avg_occupation: number; total_revenue: number }

type Resource = "kpis" | "heatmap" | "growth" | "memberEvolution" | "insights" | "coachPerformance";
type ResourceMeta = Record<Resource, { committedScope: string | null; requestedScope: string | null }>;

interface DashboardV2State {
  totalMembers: number; classesToday: number; totalBookings: number; revenue: number; newMembers: number; renewals: number; averageOccupation: number; retentionRate: number | null;
  memberBookings: number | null; trialBookings: number | null; renewalMembers: number | null;
  businessTimeZone: 'Africa/Tunis' | 'Asia/Riyadh' | null;
  kpisLoading: boolean; kpisError: string | null;
  heatmapData: HeatmapCell[]; heatmapLoading: boolean; heatmapError: string | null;
  growthData: GrowthDataPoint[]; growthLoading: boolean; growthError: string | null;
  memberEvolutionData: MemberEvolutionPoint[]; memberEvolutionLoading: boolean; memberEvolutionError: string | null;
  insights: InsightData[]; insightsLoading: boolean; insightsError: string | null;
  coachPerformanceData: CoachPerformance[]; coachPerformanceLoading: boolean; coachPerformanceError: string | null;
  activeTenant: string | null;
  resourceMeta: ResourceMeta;
  beginTenantScope: (userId: string, gymId: string) => void;
  fetchAllKPIs: (period: DashboardPeriod, range: DateRange, identity: { userId: string; gymId: string }) => Promise<void>;
  fetchHeatmap: (period: DashboardPeriod, range: DateRange, identity: { userId: string; gymId: string }) => Promise<void>;
  fetchGrowthData: (period: DashboardPeriod, range: DateRange, comparison: DateRange, identity: { userId: string; gymId: string }) => Promise<void>;
  fetchMemberEvolution: (period: DashboardPeriod, range: DateRange, identity: { userId: string; gymId: string }) => Promise<void>;
  fetchInsights: (period: DashboardPeriod, range: DateRange, identity: { userId: string; gymId: string }) => Promise<void>;
  fetchCoachPerformance: (period: DashboardPeriod, range: DateRange, identity: { userId: string; gymId: string }) => Promise<void>;
}

const EMPTY_META: ResourceMeta = {
  kpis: { committedScope: null, requestedScope: null }, heatmap: { committedScope: null, requestedScope: null },
  growth: { committedScope: null, requestedScope: null }, memberEvolution: { committedScope: null, requestedScope: null },
  insights: { committedScope: null, requestedScope: null }, coachPerformance: { committedScope: null, requestedScope: null },
};

const scopeFor = (range: DateRange, identity: { userId: string; gymId: string }): DashboardRequestScope => ({ ...identity, ...range });
const messageOf = (error: unknown) => error instanceof Error ? error.message : "Unable to load dashboard data";
const invoke = async (body: Record<string, unknown>) => {
  const { data, error } = await supabase.functions.invoke("dashboard-kpis", { body });
  if (error) throw error;
  return data;
};

export const useDashboardV2Store = create<DashboardV2State>((set, get) => {
  const request = async <T>(
    resource: Resource,
    period: DashboardPeriod,
    range: DateRange,
    identity: { userId: string; gymId: string },
    action: string,
    select: (payload: unknown) => T,
    commit: (value: T) => Partial<DashboardV2State>,
  ) => {
    const scope = scopeFor(range, identity);
    const scopeKey = dashboardScopeKey(scope);
    const loadingKey = `${resource}Loading` as keyof DashboardV2State;
    const errorKey = `${resource}Error` as keyof DashboardV2State;
    set((state) => ({
      [loadingKey]: true,
      [errorKey]: null,
      resourceMeta: { ...state.resourceMeta, [resource]: { ...state.resourceMeta[resource], requestedScope: scopeKey } },
    } as Partial<DashboardV2State>));

    const { token, promise } = runDashboardRequest(resource, scope, () => invoke({
      action, gym_id: identity.gymId, period,
      granularity: getDashboardGranularity(period), start_date: range.startDate, end_date: range.endDate,
    }), getDashboardGranularity(period));
    try {
      const payload = await promise;
      if (!isLatestDashboardRequest(resource, token) || get().activeTenant !== `${identity.userId}:${identity.gymId}`) return;
      set((state) => ({
        ...commit(select(payload)), [loadingKey]: false, [errorKey]: null,
        resourceMeta: { ...state.resourceMeta, [resource]: { committedScope: scopeKey, requestedScope: scopeKey } },
      } as Partial<DashboardV2State>));
    } catch (error) {
      if (!isLatestDashboardRequest(resource, token) || get().activeTenant !== `${identity.userId}:${identity.gymId}`) return;
      set({ [loadingKey]: false, [errorKey]: messageOf(error) } as Partial<DashboardV2State>);
    }
  };

  return {
    totalMembers: 0, classesToday: 0, totalBookings: 0, revenue: 0, newMembers: 0, renewals: 0, averageOccupation: 0, retentionRate: 0,
    memberBookings: null, trialBookings: null, renewalMembers: null, businessTimeZone: null,
    kpisLoading: false, kpisError: null,
    heatmapData: [], heatmapLoading: false, heatmapError: null,
    growthData: [], growthLoading: false, growthError: null,
    memberEvolutionData: [], memberEvolutionLoading: false, memberEvolutionError: null,
    insights: [], insightsLoading: false, insightsError: null,
    coachPerformanceData: [], coachPerformanceLoading: false, coachPerformanceError: null,
    activeTenant: null, resourceMeta: EMPTY_META,
    beginTenantScope: (userId, gymId) => set((state) => {
      const tenant = `${userId}:${gymId}`;
      if (state.activeTenant === tenant) return state;
      return {
        activeTenant: tenant, resourceMeta: EMPTY_META,
        totalMembers: 0, classesToday: 0, totalBookings: 0, revenue: 0, newMembers: 0, renewals: 0, averageOccupation: 0, retentionRate: 0,
        memberBookings: null, trialBookings: null, renewalMembers: null, businessTimeZone: null,
        heatmapData: [], growthData: [], memberEvolutionData: [], insights: [], coachPerformanceData: [],
        kpisError: null, heatmapError: null, growthError: null, memberEvolutionError: null, insightsError: null, coachPerformanceError: null,
      };
    }),
    fetchAllKPIs: (period, range, identity) => request("kpis", period, range, identity, "get_all_kpis", (p) => dashboardKPIsSchema.parse(p), (data) => ({
      totalMembers: Number(data.totalMembers ?? 0), classesToday: Number(data.classesToday ?? 0), totalBookings: Number(data.totalBookings ?? 0),
      revenue: Number(data.revenue ?? 0), newMembers: Number(data.newMembers ?? 0), renewals: Number(data.renewals ?? 0),
      averageOccupation: data.averageOccupation, retentionRate: data.retentionRate,
      memberBookings: data.memberBookings ?? null, trialBookings: data.trialBookings ?? null, renewalMembers: data.renewalMembers ?? null,
      businessTimeZone: data.businessTimeZone ?? null,
    })),
    fetchHeatmap: (period, range, identity) => request("heatmap", period, range, identity, "get_heatmap", (p) => dashboardHeatmapSchema.parse(p).data as HeatmapCell[], (heatmapData) => ({ heatmapData })),
    fetchGrowthData: async (period, range, comparison, identity) => {
      const scope = scopeFor(range, identity);
      const resource = "growth" as const;
      const scopeKey = dashboardScopeKey(scope);
      set((state) => ({ growthLoading: true, growthError: null, resourceMeta: { ...state.resourceMeta, growth: { ...state.resourceMeta.growth, requestedScope: scopeKey } } }));
      const { token, promise } = runDashboardRequest(resource, scope, () => invoke({ action: "get_growth_data", gym_id: identity.gymId, period, granularity: getDashboardGranularity(period), start_date: range.startDate, end_date: range.endDate, comparison_start_date: comparison.startDate, comparison_end_date: comparison.endDate }), `${getDashboardGranularity(period)}:${comparison.startDate}:${comparison.endDate}`);
      try {
        const payload = await promise;
        if (!isLatestDashboardRequest(resource, token) || get().activeTenant !== `${identity.userId}:${identity.gymId}`) return;
        const points = dashboardGrowthSchema.parse(payload).data as GrowthDataPoint[];
        set((state) => ({ growthData: points, growthLoading: false, growthError: null, resourceMeta: { ...state.resourceMeta, growth: { committedScope: scopeKey, requestedScope: scopeKey } } }));
      } catch (error) {
        if (!isLatestDashboardRequest(resource, token) || get().activeTenant !== `${identity.userId}:${identity.gymId}`) return;
        set({ growthLoading: false, growthError: messageOf(error) });
      }
    },
    fetchMemberEvolution: (period, range, identity) => request("memberEvolution", period, range, identity, "get_member_evolution", (p) => dashboardMemberEvolutionSchema.parse(p).points as MemberEvolutionPoint[], (memberEvolutionData) => ({ memberEvolutionData })),
    fetchInsights: (period, range, identity) => request("insights", period, range, identity, "get_insights", (p) => dashboardInsightsSchema.parse(p).data as InsightData[], (insights) => ({ insights })),
    fetchCoachPerformance: (period, range, identity) => request("coachPerformance", period, range, identity, "get_coach_performance", (p) => dashboardCoachPerformanceSchema.parse(p).data as CoachPerformance[], (coachPerformanceData) => ({ coachPerformanceData })),
  };
});